import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl, uploadUrl, asArray } from "../lib/api";
import { HERO_FALLBACK_IMAGE, workCoverUrl } from "../lib/media";
import { useI18n } from "../contexts/I18nContext";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { profilePdfPath } from "../lib/profile";
import useReveal from "../hooks/useReveal";
import CountUp from "../components/CountUp";
import WorldMapInteractive from "../components/WorldMapInteractive";
import * as LucideIcons from "lucide-react";
import { ArrowRight, ArrowLeft, Download } from "lucide-react";

export default function HomePage() {
    const { t, pick, isRTL, lang } = useI18n();
    const { settings } = useSiteSettings();
    const Arrow = isRTL ? ArrowLeft : ArrowRight;
    const ref = useReveal();
    const [works, setWorks] = useState([]);
    const [news, setNews] = useState([]);
    const [stats, setStats] = useState([]);
    const [services, setServices] = useState([]);
    const [clients, setClients] = useState([]);
    const [profile, setProfile] = useState(null);

    useEffect(() => {
        api.get("/works?featured=true&limit=4")
            .then((r) => setWorks(asArray(r.data, "works")))
            .catch(() => setWorks([]));
        api.get("/news?limit=3")
            .then((r) => setNews(asArray(r.data, "news")))
            .catch(() => setNews([]));
        api.get("/stats")
            .then((r) => setStats(
                asArray(r.data, "stats").filter((s) => !(s.category || "").toLowerCase().includes("influencer"))
            ))
            .catch(() => setStats([]));
        api.get("/services")
            .then((r) => setServices(asArray(r.data, "services").slice(0, 6)))
            .catch(() => setServices([]));
        api.get("/clients")
            .then((r) => setClients(asArray(r.data, "clients").slice(0, 12)))
            .catch(() => setClients([]));
        api.get("/company-profile").then((r) => setProfile(r.data)).catch(() => {});
    }, []);

    const heroFallback = {
        en: {
            title: "Saudi’s Premier PR & Communication Partner",
            subtitle: "Bridging Cultures Through Meaningful Connections",
            tagline: "Insight. Influence. Legacy.",
        },
        ar: {
            title: "الشريك السعودي الأول للعلاقات العامة والاتصال",
            subtitle: "نبني جسور الثقافات عبر علاقات ذات معنى",
            tagline: "بصيرة. تأثير. إرث.",
        },
    };
    const heroText = (field) => {
        const localized = settings[`hero_${field}_${lang}`];
        const fallback = heroFallback[lang]?.[field] || heroFallback.en[field];
        return localized && localized.trim() ? localized : fallback;
    };
    const heroTitle = heroText("title");
    const heroSubtitle = heroText("subtitle");
    const heroTagline = heroText("tagline");
    const profilePath = profilePdfPath(profile, lang);
    const heroVideo = uploadUrl("Website-Cover-Final.mp4");
    const aboutTeaser = t("section_about_body").split(/\n{2,}/)[0];
    return (
        <div ref={ref} data-testid="home-page">
            {/* HERO */}
            <section className="relative h-[100svh] min-h-[660px] overflow-hidden bg-[#002951] text-white">
                {/* Clean cosmic gradient backdrop with subtle starfield (no skyline image — removed visual artifacts) */}
                <img
                    src={HERO_FALLBACK_IMAGE}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 h-full w-full object-cover"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                />
                <video
                    className="absolute inset-0 h-full w-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    poster={HERO_FALLBACK_IMAGE}
                    aria-hidden="true"
                >
                    <source src={heroVideo} type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-r from-[#001b35]/90 via-[#002951]/58 to-[#001b35]/25" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#001b35]/92 via-transparent to-black/20" />
                <div className="grain-overlay" />

                <div className="relative h-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 flex flex-col justify-end pb-16 pt-24 sm:pb-20 md:pb-24 lg:pb-28">
                    <div
                        className={`hero-copy reveal ${isRTL ? "max-w-4xl text-right ml-auto" : "max-w-6xl text-left"}`}
                        dir={isRTL ? "rtl" : "ltr"}
                        lang={lang}
                    >
                        <h1 className="heading hero-title text-balance" data-testid="hero-title">
                            {heroTitle}
                        </h1>
                        <p className="hero-subtitle max-w-4xl" data-testid="hero-subtitle">
                            {heroSubtitle}
                        </p>
                        <p className="hero-tagline max-w-3xl" data-testid="hero-tagline">
                            {heroTagline}
                        </p>
                    </div>
                    <div className={`reveal mt-8 sm:mt-10 flex flex-wrap gap-3 sm:gap-4 ${isRTL ? "justify-end" : ""}`}>
                        {profilePath && (
                            <a
                                href={fileUrl(profilePath)}
                                target="_blank" rel="noreferrer"
                                data-testid="hero-cta-profile"
                                className="inline-flex items-center gap-2 sm:gap-3 bg-[#0060A7] text-white hover:bg-white hover:text-[#002951] px-6 sm:px-8 py-4 sm:py-5 text-xs sm:text-sm uppercase tracking-[0.15em] sm:tracking-[0.2em] transition-all duration-300 hover:shadow-lg"
                            >
                                <Download size={16} className="flex-shrink-0" />
                                <span className="hidden sm:inline">{(lang === "ar" ? profile.button_label_ar : profile.button_label_en) || t("cta_company_profile")}</span>
                                <span className="sm:hidden">{t("cta_company_profile").split(" ")[0]}</span>
                            </a>
                        )}
                        <Link
                            to="/work"
                            data-testid="hero-cta-work"
                            className="group inline-flex items-center gap-2 sm:gap-3 border border-white/30 hover:bg-white hover:text-[#002951] px-6 sm:px-8 py-4 sm:py-5 text-xs sm:text-sm uppercase tracking-[0.15em] sm:tracking-[0.2em] transition-all duration-300 hover:shadow-lg hover:border-white"
                        >
                            {t("cta_explore")}
                            <Arrow size={16} className="transition-transform group-hover:translate-x-1 flex-shrink-0" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ABOUT TEASER */}
            <section className="bg-white py-16 sm:py-24 lg:py-32" data-testid="home-about">
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-12 gap-6 sm:gap-8">
                    <div className="col-span-12 md:col-span-5 reveal">
                        <div className="section-eyebrow mb-4 plus-mark">
                            {t("section_about_overline")}
                        </div>
                        <h2 className="heading page-hero-oneline">
                            {t("section_about_title")}
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-7 reveal">
                        <p className="text-base sm:text-lg md:text-xl text-[#1f2937] leading-relaxed">
                            {aboutTeaser}
                        </p>
                        <Link to="/about" className="legends-link inline-flex items-center gap-2 mt-6 sm:mt-8 text-xs sm:text-sm uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[#002951] hover:text-[#0060A7]" data-testid="home-about-link">
                            {t("cta_read_more")} <Arrow size={16} className="flex-shrink-0" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* NETWORK / NUMBERS */}
            <section className="bg-[#002951] text-white py-16 sm:py-24 lg:py-32 relative overflow-hidden" data-testid="home-numbers">
                <div className="grain-overlay" />
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 relative">
                    <div className="section-eyebrow section-eyebrow-dark mb-4 plus-mark">
                        {t("section_numbers_overline")}
                    </div>
                    <h2 className="heading page-hero-oneline max-w-full mb-12 sm:mb-16 reveal">
                        {t("section_numbers_title")}
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 sm:gap-x-8 gap-y-10 sm:gap-y-14">
                        {stats.map((s, i) => {
                            const Icon = LucideIcons[s.icon] || LucideIcons.Sparkles;
                            return (
                                <div key={s.id || i} className="text-center md:text-start" data-testid={`stat-${i}`}>
                                    <Icon size={32} strokeWidth={1.3} className="text-[#81D3EB] mb-3 sm:mb-5 mx-auto md:mx-0" />
                                    <div className="heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight font-black leading-none">
                                        <CountUp value={s.value} duration={2200} />
                                    </div>
                                    <div className="text-xs sm:text-sm md:text-base text-white/70 mt-2 sm:mt-3">{pick(s, "label")}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="bg-[#EFF0F1] py-16 sm:py-24 lg:py-32" data-testid="home-services">
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 sm:gap-6 mb-12 sm:mb-16">
                        <div className="reveal w-full">
                            <div className="section-eyebrow mb-3 sm:mb-4 plus-mark">
                                {t("section_services_overline")}
                            </div>
                            <h2 className="heading page-hero-oneline">
                                {t("section_services_title")}
                            </h2>
                        </div>
                        <Link to="/services" className="legends-link text-xs sm:text-sm uppercase tracking-[0.15em] sm:tracking-[0.2em] text-[#002951] inline-flex items-center gap-2 reveal mt-4 md:mt-0">
                            {t("cta_view_all")} <Arrow size={16} className="flex-shrink-0" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {services.map((s, i) => (
                            <div key={s.id} className="service-card reveal" data-testid={`service-card-${i}`}>
                                <div className="service-number">0{i + 1}</div>
                                <h3 className="heading text-lg sm:text-xl md:text-2xl mt-6 sm:mt-8 mb-2 sm:mb-3 tracking-tight font-bold text-[#002951]">
                                    {pick(s, "title")}
                                </h3>
                                <p className="text-[#374151] text-sm sm:text-base leading-relaxed">{pick(s, "description")}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CLIENTS */}
            {clients.length > 0 && (
                <section className="clients-home-showcase py-16 sm:py-24 lg:py-32" data-testid="home-clients">
                    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12">
                        <div className="clients-home-header reveal">
                            <div className="max-w-3xl">
                                <h2 className="heading page-hero-oneline text-[#002951]">
                                    {lang === "ar" ? "موثوقون من قبل شركات رائدة" : "Trusted by Leading Companies"}
                                </h2>
                            </div>
                            <Link to="/clients" className="client-showcase-link reveal flex-shrink-0">
                                {t("cta_view_all")} <Arrow size={16} className="flex-shrink-0" />
                            </Link>
                        </div>
                        <div className="clients-logo-grid reveal" data-testid="home-clients-grid">
                            {clients.map((client, i) => {
                                const ClientTag = client.website ? "a" : "div";
                                return (
                                    <ClientTag
                                        key={client.id || pick(client, "name") || i}
                                        href={client.website || undefined}
                                        target={client.website ? "_blank" : undefined}
                                        rel={client.website ? "noopener noreferrer" : undefined}
                                        className="client-logo-tile group"
                                        data-testid={`home-client-${i}`}
                                    >
                                        {client.logo ? (
                                            <img
                                                src={fileUrl(client.logo)}
                                                alt={pick(client, "name")}
                                                loading="lazy"
                                                decoding="async"
                                                className="client-logo-image"
                                            />
                                        ) : (
                                            <span className="client-logo-name">
                                                {pick(client, "name")}
                                            </span>
                                        )}
                                    </ClientTag>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* GLOBAL REACH */}
            <section className="bg-[#002951] text-white py-24 md:py-32 relative overflow-hidden" data-testid="home-global-reach">
                <div className="grain-overlay" />
                <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
                        <div className="lg:col-span-5 reveal">
                            <div className="section-eyebrow section-eyebrow-dark mb-5 plus-mark">
                                {t("section_global_overline")}
                            </div>
                            <h2 className="heading page-hero-oneline mb-6">
                                {t("section_global_title")}
                            </h2>
                            <p className="text-base md:text-lg text-white/70 leading-relaxed max-w-md mb-8">
                                {t("section_global_sub")}
                            </p>
                            <Link
                                to="/global-reach"
                                className="inline-flex items-center gap-3 bg-white text-[#002951] hover:bg-[#0060A7] hover:text-white px-7 py-4 text-sm uppercase tracking-[0.2em] transition-colors"
                                data-testid="global-reach-cta"
                            >
                                {t("cta_explore_more")}
                                <Arrow size={16} strokeWidth={2.4} />
                            </Link>
                        </div>
                        <div className="lg:col-span-7 reveal">
                            <WorldMapInteractive />
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURED WORKS */}
            <section className="bg-white py-24 md:py-32" data-testid="home-works">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                        <div className="reveal">
                            <div className="section-eyebrow mb-4 plus-mark">
                                {t("section_works_overline")}
                            </div>
                            <h2 className="heading page-hero-oneline">
                                {t("section_works_title")}
                            </h2>
                        </div>
                        <Link to="/work" className="legends-link text-sm uppercase tracking-[0.2em] text-[#002951] inline-flex items-center gap-2 reveal">
                            {t("cta_view_all")} <Arrow size={16} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-12 gap-4">
                        {works.map((w, i) => (
                            <Link
                                key={w.id}
                                to={`/work/${w.slug}`}
                                    className={`group relative overflow-hidden bg-[#EFF0F1] aspect-[16/9] reveal ${
                                        i === 0 ? "col-span-12 md:col-span-8 md:aspect-[16/10]" : "col-span-6 md:col-span-4"
                                    }`}
                                data-testid={`featured-work-${i}`}
                            >
                                {workCoverUrl(w) ? (
                                    <img
                                        src={workCoverUrl(w)}
                                        alt={pick(w, "title")}
                                        loading={i < 2 ? "eager" : "lazy"}
                                        decoding="async"
                                        className="absolute inset-0 w-full h-full object-contain p-2 transition-transform duration-700 group-hover:scale-105"
                                    />
                                ) : (
                                    <div className="absolute inset-0 bg-[#002951]" />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-[#002951] via-[#002951]/20 to-transparent" />
                                    <div className="absolute inset-0 bg-[#002951]/0 group-hover:bg-[#002951]/65 transition-colors duration-500" />
                                <div className="relative h-full flex flex-col justify-end p-6 md:p-10 text-white">
                                    <div className="text-xs uppercase tracking-[0.3em] text-white/70 mb-3">
                                        {pick(w, "category")}
                                    </div>
                                    <h3 className="heading text-xl md:text-2xl lg:text-3xl tracking-tight leading-tight max-w-xl font-bold">
                                        {pick(w, "title")}
                                    </h3>
                                    <div className="mt-4 opacity-0 group-hover:opacity-100 -translate-y-2 group-hover:translate-y-0 transition-all duration-300 inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em]">
                                        {t("cta_view_case")} <Arrow size={14} />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* NEWS */}
            <section className="bg-[#EFF0F1] py-24 md:py-32" data-testid="home-news">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                        <div className="reveal">
                            <div className="section-eyebrow mb-4 plus-mark">
                                {t("section_news_overline")}
                            </div>
                            <h2 className="heading page-hero-oneline">
                                {t("section_news_title")}
                            </h2>
                        </div>
                        <Link to="/news" className="legends-link text-sm uppercase tracking-[0.2em] text-[#002951] inline-flex items-center gap-2 reveal">
                            {t("cta_view_all")} <Arrow size={16} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {news.map((n, i) => (
                            <Link
                                key={n.id}
                                to={`/news/${n.slug}`}
                                className="group reveal bg-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                                data-testid={`news-card-${i}`}
                            >
                                <div className="aspect-[16/9] overflow-hidden bg-white">
                                    {n.cover_image && (
                                        <img src={fileUrl(n.cover_image)} alt={pick(n, "title")} loading="lazy" decoding="async" className="w-full h-full object-contain p-2 transition-transform duration-700 group-hover:scale-105" />
                                    )}
                                </div>
                                <div className="p-6">
                                    <div className="text-xs uppercase tracking-[0.25em] text-[#0060A7] mb-3">
                                        {pick(n, "category")}
                                    </div>
                                    <h3 className="heading text-lg md:text-xl tracking-tight leading-tight font-bold">
                                        {pick(n, "title")}
                                    </h3>
                                    <div className="text-xs text-[#6B7280] mt-4">
                                        {new Date(n.published_at).toLocaleDateString(isRTL ? "ar" : "en")}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
